import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Building2,
  Settings,
  Headphones,
  Copyright,
} from "lucide-react";
import OptimizedImg from "./OptimizedImg";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const dividerColor = "rgba(124,58,237,0.15)";

  const location = useLocation();
  const navigate = useNavigate();

  const handleHomeClick = () => {
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  const handleNavClick = (path) => {
    if (location.pathname === path) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate(path);
    }
  };

  const handleProductClick = (slug) => {
    const path = `/products/${slug}`;
    if (location.pathname === path) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate(path);
    }
  };

  const companyLinks = [
    { label: "About Us", action: () => handleNavClick("/about") },
    { label: "Contact Us", action: () => handleNavClick("/contact-us") },
    { label: "Privacy Policy", action: () => handleNavClick("/PrivacyPolicy") },
    { label: "Terms & Conditions", action: () => handleNavClick("/TermsCondition") },
  ];

  const productLinks = [
    { label: "Rent & Property Management", action: () => handleProductClick("rent-property-management-solution") },
    { label: "Medical Equipment Management", action: () => handleProductClick("hospital-equipment-management") },
    { label: "Printing Management System", action: () => handleProductClick("printing-management-system") },
    { label: "Electrical Panel Management", action: () => handleProductClick("electrical-panel-management") },
    { label: "Sagveek Learning Management", action: () => handleProductClick("learning-management-system") },
    { label: "View All Products", action: () => handleNavClick("/products") },
  ];

  const serviceLinks = [
    { label: "Edtech Solutions", action: () => handleNavClick("/courses") },
    { label: "Web Development", action: () => handleNavClick("/services/web-development") },
    { label: "Mobile App Development", action: () => handleNavClick("/services/mobile-app-development") },
    { label: "Custom Software Development", action: () => handleNavClick("/services/custom-software-development") },
  ];

  return (
    <>
      <style>
        {`
          .sf-footer {
            position: relative;
            overflow: hidden;
            background-color: #faf8ff;
            color: #1f2937;
          }

          .sf-wave {
            position: absolute;
            right: 0;
            bottom: 0;
            width: 55%;
            max-width: 900px;
            height: auto;
            pointer-events: none;
            z-index: 0;
            opacity: 0.9;
          }

          .sf-footer .container {
            position: relative;
            z-index: 1;
          }

          .sf-tagline {
            font-family: 'Inter', sans-serif;
            color: #6b7280;
            font-size: 0.88rem;
            line-height: 1.4;
            margin-top: 8px;
            margin-bottom: 12px;
          }

          .sf-social-btn {
            width: 36px;
            height: 36px;
            border-radius: 50%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background-color: rgba(124,58,237,0.12);
            color: #7c3aed;
            border: none;
            transition: all 0.3s ease;
            cursor: pointer;
          }

          .sf-social-btn:hover {
            background-color: #7c3aed;
            color: #fff;
            transform: translateY(-3px);
          }

          .sf-heading-row {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-bottom: 12px;
          }

          .sf-heading-icon {
            width: 32px;
            height: 32px;
            min-width: 32px;
            border-radius: 50%;
            background-color: rgba(124,58,237,0.12);
            color: #7c3aed;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }

          .sf-heading-text {
            font-family: 'Outfit', sans-serif;
            font-weight: 800;
            color: #5b21b6;
            margin: 0;
            font-size: 0.98rem;
          }

          .sf-link-list {
            list-style: none;
            padding: 0;
            margin: 0;
          }

          .sf-link-row {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 5px 2px;
            font-family: 'Inter', sans-serif;
            color: #374151;
            font-weight: 400;
            font-size: 0.92rem;
            cursor: pointer;
            transition: color 0.25s ease, padding-left 0.25s ease;
          }

          .sf-link-row:hover {
            color: #7c3aed;
            padding-left: 6px;
          }

          .sf-col-divider {
            border-right: 1px solid ${dividerColor};
          }

          @media (max-width: 767.98px) {
            .sf-col-divider {
              border-right: none;
              border-bottom: 1px solid ${dividerColor};
              padding-bottom: 14px;
              margin-bottom: 14px;
            }
            .sf-link-row {
              justify-content: center;
            }
            .sf-heading-row {
              justify-content: center;
            }
          }

          .sf-bottom-bar {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 8px 0;
          }

          .sf-copyright {
            display: flex;
            align-items: center;
            gap: 10px;
            font-family: 'Inter', sans-serif;
            color: #6b7280;
            font-size: 0.9rem;
          }

          .sf-copyright-icon {
            width: 22px;
            height: 22px;
            min-width: 22px;
            border-radius: 50%;
            background-color: rgba(124,58,237,0.12);
            color: #7c3aed;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }

          .sf-copyright span.sf-brand {
            color: #5b21b6;
            font-weight: 700;
          }
        `}
      </style>

      <footer className="sf-footer pt-4 pb-1">
        <svg
          className="sf-wave"
          viewBox="0 0 800 300"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0,300 C150,180 300,260 450,190 C600,120 700,180 800,140 L800,300 Z"
            fill="rgba(124,58,237,0.06)"
          />
          <path
            d="M100,300 C250,220 380,280 520,220 C650,165 720,220 800,190 L800,300 Z"
            fill="rgba(124,58,237,0.09)"
          />
        </svg>

        <div className="container">
          <div className="row text-md-start text-center">

            <div className="col-md-3 pb-4 pb-md-0 sf-col-divider">
              <div onClick={handleHomeClick} style={{ cursor: "pointer" }}>
                <OptimizedImg
                  alt="Logo"
                  src="/images/sn_logo.png"
                  width={190}
                  height={60}
                  loading="lazy"
                  decoding="async"
                  style={{ width: "190px", height: "auto", cursor: "pointer" }}
                  avif={false}
                />
              </div>

              <p className="sf-tagline">
                Innovative Technology Solutions<br />for a Smarter Tomorrow
              </p>

              <div className="d-flex gap-2 justify-content-center justify-content-md-start">
                <a href="https://www.facebook.com/share/rmWgvDky6juJpXNP/?mibextid=qi2Omg" className="sf-social-btn" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><i className="fa-brands fa-facebook-f"></i></a>
                <a href="https://www.instagram.com/sagveek_technologies" className="sf-social-btn" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><i className="fa-brands fa-instagram"></i></a>
                <a href="https://x.com/sagveek_tech" className="sf-social-btn" target="_blank" rel="noopener noreferrer" aria-label="X"><i className="fa-brands fa-x-twitter"></i></a>
                <a href="https://www.linkedin.com/company/sagveek-technologies/" className="sf-social-btn" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><i className="fa-brands fa-linkedin-in"></i></a>
              </div>
            </div>

            <div className="col-md-3 pb-4 pb-md-0 sf-col-divider">
              <div className="sf-heading-row">
                <span className="sf-heading-icon"><Building2 size={18} /></span>
                <h5 className="sf-heading-text">Company</h5>
              </div>
              <ul className="sf-link-list">
                {companyLinks.map((item) => (
                  <li key={item.label} className="sf-link-row" onClick={item.action}>
                    <span>{item.label}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="col-md-3 pb-4 pb-md-0 sf-col-divider">
              <div className="sf-heading-row">
                <span className="sf-heading-icon"><Settings size={18} /></span>
                <h5 className="sf-heading-text">Products</h5>
              </div>
              <ul className="sf-link-list">
                {productLinks.map((item) => (
                  <li key={item.label} className="sf-link-row" onClick={item.action}>
                    <span>{item.label}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="col-md-3 pb-4 pb-md-0">
              <div className="sf-heading-row">
                <span className="sf-heading-icon"><Headphones size={18} /></span>
                <h5 className="sf-heading-text">Services</h5>
              </div>
              <ul className="sf-link-list">
                {serviceLinks.map((item) => (
                  <li key={item.label} className="sf-link-row" onClick={item.action}>
                    <span>{item.label}</span>
                  </li>
                ))}
              </ul>
            </div>

          </div>

          <hr className="my-2" style={{ borderColor: dividerColor }} />

          <div className="sf-bottom-bar">
            <div className="sf-copyright">
              <span className="sf-copyright-icon"><Copyright size={14} /></span>
              <span>
                {currentYear} All rights reserved. Designed &amp; Developed by{" "}
                <span className="sf-brand" onClick={handleHomeClick} style={{ cursor: "pointer" }}>
                  Sagveek Technologies Pvt. Ltd.
                </span>
              </span>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;